import { useCallback } from "react"

/**
 * Custom hook for managing navigation in the help center
 *
 * @param {Object} contentLoader - The content loader hooks
 * @returns {Object} Navigation functions
 */
export function useNavigation(contentLoader) {
  const {
    loadArticle,
    loadCategory,
    setExpandedCategories,
    expandedCategories,
    setActiveContentSlug,
    setActiveContentType,
    setSelectedContent,
    setContentType,
    setBreadcrumbItems,
    updateUrl,
    loadContentFromUrl,
  } = contentLoader

  /**
   * Navigate to content programmatically
   */
  const navigateTo = useCallback(
    async (type, slug = null, parentPath = "") => {
      try {
        if (type === "article") {
          // Determine full path for expanding correct categories
          const fullCategoryPath = parentPath ? parentPath : ""

          // First expand categories in the path to ensure sidebar is updated
          if (fullCategoryPath) {
            const pathParts = fullCategoryPath.split("/")
            const newExpandedCategories = { ...expandedCategories }

            for (let i = 0; i < pathParts.length; i++) {
              newExpandedCategories[pathParts[i]] = true
            }

            setExpandedCategories(newExpandedCategories)
          }

          // Then load article
          const articleData = await loadArticle(slug, parentPath)
          if (articleData) {
            // Update URL first, then load content
            updateUrl("article", slug, parentPath)
            setActiveContentSlug(slug)
            setActiveContentType("article")
            setSelectedContent(articleData)
            setContentType("article")
          }
        } else if (type === "category") {
          const fullPath = parentPath ? `${parentPath}/${slug}` : slug

          // First expand the categories in the path
          if (fullPath) {
            const pathParts = fullPath.split("/")
            const newExpandedCategories = { ...expandedCategories }

            for (let i = 0; i < pathParts.length; i++) {
              newExpandedCategories[pathParts[i]] = true
            }

            setExpandedCategories(newExpandedCategories)
          }

          // Then load category
          const categoryData = await loadCategory(fullPath)
          if (categoryData) {
            // Update URL first, then load content
            updateUrl("category", slug, parentPath)
            setActiveContentSlug(fullPath)
            setActiveContentType("category")
            setSelectedContent(categoryData)
            setContentType("category")
          }
        } else {
          // Reset to home state
          setSelectedContent(null)
          setContentType(null)
          setBreadcrumbItems([{ label: "Help Center", link: "/help-center" }])
          updateUrl(null)
          setActiveContentSlug(null)
          setActiveContentType(null)
        }
      } catch (err) {
        console.error("Error navigating to content:", err)
      }
    },
    [
      expandedCategories,
      loadArticle,
      loadCategory,
      setActiveContentSlug,
      setActiveContentType,
      setContentType,
      setExpandedCategories,
      setSelectedContent,
      setBreadcrumbItems,
      updateUrl,
    ]
  )

  /**
   * Handle article selection from browser
   */
  const handleArticleSelect = useCallback(
    async (article) => {
      if (!article || !article.slug) return
      await navigateTo("article", article.slug, article.categoryPath)
    },
    [navigateTo]
  )

  /**
   * Handle category selection from browser
   */
  const handleCategorySelect = useCallback(
    async (category) => {
      if (!category || !category.slug) return
      // Update expanded state
      setExpandedCategories((prev) => ({
        ...prev,
        [category.slug]: category.shouldExpand,
      }))

      // If we should navigate to the category content
      if (category.shouldExpand) {
        const categoryPath = category.parentPath
          ? `${category.parentPath}/${category.slug}`
          : category.slug

        await navigateTo("category", category.slug, category.parentPath)
      }
    },
    [navigateTo, setExpandedCategories]
  )

  return {
    navigateTo,
    handleArticleSelect,
    handleCategorySelect,
    // Export this function for direct URL-based loading
    loadContentFromUrl,
  }
}

export default useNavigation
